/**
 * Epic 10 — de vastgestelde schermteksten bij bijzonder verlof (§10.3, §10.4
 * en §10.5).
 *
 * Alle zinnen staan hier op ÉÉN plek en niet verspreid door de JSX, zodat KE-7
 * met één blik na te lopen is: geen enkele regel hieronder is onderweg bedacht.
 * Dezelfde vorm als `verlofsoortKeuzeTeksten.ts` van story 5.2.
 *
 * WAT HIER NIET STAAT: de tien optieteksten van het handboek. Die wonen in
 * `BIJZONDER_VERLOF_GEBEURTENISSEN` in `packages/shared` en staan daar op de
 * enige plek waar het handboek in de codebase mag staan (AD-38). Een tweede
 * afschrift hier zou precies de tweede lijst zijn die AC-1 van story 10.1
 * uitsluit.
 */

import { datumInWoorden } from '@functieloonhuis/shared';

/** §10.3 — het label van de gebeurteniskeuze. */
export const GEBEURTENIS_VRAAG = 'Om welke gebeurtenis gaat het?';

/** §10.3 — de toelichting bij het voorgevulde voorstel voor de periode. */
export const VOORSTEL_TOELICHTING =
  'We vullen dit alvast voor je in. Klopt het niet, pas het dan aan — HR kijkt er sowieso naar.';

/**
 * §10.3 — de twee aanvullende bepalingen bij de regeling.
 *
 * Ze horen bij de REGELING en niet bij één regel: ze staan er onafhankelijk van
 * welke gebeurtenis gekozen is, en zonder verzonnen kopje erboven (§10.3 geeft
 * er geen).
 */
export const AANVULLENDE_BEPALINGEN = [
  'Dit geldt ook bij een geregistreerd partnerschap of als je aantoonbaar duurzaam samenwoont.',
  'Onder kinderen vallen ook pleegkinderen en stiefkinderen.',
] as const;

/** §10.3 — bijzonder verlof gaat altijd langs HR (FR-8, besluit V5). */
export const ALTIJD_LANGS_HR =
  'Bijzonder verlof wordt altijd door HR beoordeeld, hoeveel dagen het ook betreft.';

/** §10.3 — waarom de periode minder verlof oplevert dan er kalenderdagen zijn (FR-13). */
export const SAMENLOOP_UITLEG =
  'Dagen waarop je toch al niet werkt — je vrije dag, het weekend, een feestdag — leveren geen extra verlof op en schuiven niet door.';

/**
 * Een datum als *"29 januari"* — dag en maand voluit, zonder jaartal, zoals
 * §10.3 de uitkomstregel geeft.
 *
 * DE MAANDNAMEN KOMEN UIT `packages/shared` en staan hier niet nog een keer:
 * die lijst bestaat in deze codebase al vier keer, en er komt er geen vijfde
 * bij (story 6.1, taak 2).
 *
 * §10.6 kort de maandnaam juist af (*"29 jan"*) in de historie. Allebei zijn ze
 * vastgesteld en allebei blijven ze zoals ze zijn (KE-7); wil de opdrachtgever
 * één vorm, dan verandert §10 en niet dit bestand.
 */
export function datumVoluitZonderJaar(isoDatum: string): string {
  return datumInWoorden(isoDatum);
}

/**
 * §10.3 — de uitkomst van de periode in woorden.
 *
 * Alleen de twee datums, het aantal werkdagen en het aantal uren worden
 * ingevuld; de zin zelf verandert niet (KE-7).
 *
 * EÉN WERKDAG IS GEEN "1 werkdagen" (code review 2026-08-17). Zes van de tien
 * gebeurtenissen geven precies één werkdag, dus dit is niet de uitzondering
 * maar het gewone geval. Dit is een TAALFOUT en geen nieuwe tekst: alleen het
 * telwoord buigt mee, de rest van de zin blijft staan zoals §10.3 hem geeft.
 *
 * DE UREN BUIGEN NIET MEE, en dat is geen vergeetachtigheid: *uur* is in het
 * Nederlands na een telwoord altijd enkelvoud — *1 uur*, *8 uur* — dus daar
 * valt niets recht te zetten.
 */
export function uitkomstZin(
  vanaf: string,
  totEnMet: string,
  werkdagen: number,
  uren: string
): string {
  const werkdagWoord = werkdagen === 1 ? 'werkdag' : 'werkdagen';
  return `Van ${datumVoluitZonderJaar(vanaf)} tot en met ${datumVoluitZonderJaar(
    totEnMet
  )} zijn dat voor jou ${werkdagen} ${werkdagWoord}, samen ${uren} uur.`;
}

// ── §10.4: een begrafenisdatum die nog niet vaststaat (story 10.4) ──

/** §10.4 — het vinkje zelf. */
export const VINKJE_BEGRAFENISDATUM = 'De datum van de begrafenis of crematie staat nog niet vast';

/** §10.4 — de uitleg onder het vinkje. */
export const VINKJE_UITLEG =
  'We vullen alvast het maximum in. HR past de einddatum aan zodra de datum bekend is.';

/**
 * Wat er staat wanneer die voorvulling NIET kan (code review 2026-08-17).
 *
 * Het vinkje belooft *"We vullen alvast het maximum in"*, maar zonder rooster
 * valt er niets te berekenen en gebeurde er tot deze correctie helemaal niets —
 * stil. Naar het model van de maandruimte-melding van story 6.2: informatief,
 * geen waarschuwingsvorm, en de indienknop blijft gewoon aan.
 *
 * **Vastgesteld door de opdrachtgever op 2026-08-17**, ongewijzigd zoals hij
 * hier stond. §10.4 kende deze zin niet: de opdrachtgever had besloten *dát*
 * er een melding komt zonder de woorden erbij te geven, en die zijn hem
 * daarna voorgelegd. Vanaf nu bindend onder KE-7, gelijk aan de rest van §10 —
 * over te nemen zoals hij er staat, niet onderweg herformuleren.
 */
export const VOORVULLING_MISLUKT =
  'We konden je rooster even niet ophalen. Vul de einddatum zelf in; HR past hem aan zodra de datum bekend is.';

/** §10.4 — de markering, zichtbaar voor de medewerker, zijn leidinggevende én HR. */
export const MARKERING_EINDDATUM_VOORLOPIG = 'Einddatum nog niet definitief';

/** §10.4 — wat de medewerker erbij ziet. */
export const ZIN_VOOR_MEDEWERKER =
  'Zodra de datum bekend is, past HR je verlof aan. Geef het door aan HR.';

/** §10.4 — de eerste van de twee regels die bij HR bovenop die van §10.5 komen. */
export const REGEL_VOOR_HR =
  'De begrafenisdatum stond bij het indienen nog niet vast. Pas de einddatum aan voordat je goedkeurt.';

/** §10.4 — en de tweede: aanhouden mag, er is nog niets geboekt (AD-30). */
export const AANHOUDEN_MAG =
  'Je kunt deze aanvraag laten liggen tot de datum bekend is. Er is nog niets naar de loonadministratie gegaan.';

/**
 * Story 10.4, AC-6 — wat er bij het vaststellen van de einddatum extra wordt
 * aangekondigd wanneer de aanvraag de markering droeg.
 *
 * VASTGESTELD DOOR DE OPDRACHTGEVER OP 2026-08-17 (KE-7). Het was een voorstel:
 * §10.6 geeft alleen de historieregel *"HR paste de periode aan van … naar …"*
 * en de epic vroeg de toevoeging zonder er woorden bij te geven (bevinding 2 van
 * story 10.4). Die woorden liggen nu vast en zijn dezelfde als de staart aan de
 * historieregel in de dienstlaag.
 */
export const EINDDATUM_WORDT_DEFINITIEF = 'De einddatum staat daarmee vast.';

// ── §10.3/§10.5: wat HR en de leidinggevende zien (story 10.3) ──

/** §10.5 — de waarschuwingszin voor de leidinggevende (FR-8, besluit V5). */
export const LEIDINGGEVENDE_LANGS_HR =
  'Bijzonder verlof gaat na jouw goedkeuring altijd nog langs HR.';

/**
 * §10.3 — het verhuis-signaal aan HR. Een SIGNAAL en geen blokkade: er is geen
 * foutcode en de aanvraag blijft gewoon goed te keuren.
 *
 * Enkelvoud, zoals §10.3 hem geeft. Zijn er meer eerdere verhuisdagen in
 * hetzelfde jaar, dan toont de portal de meest recente — er wordt hier geen
 * meervoudsvorm bedacht (KE-7).
 */
export function verhuisSignaalZin(isoDatum: string): string {
  return `Deze medewerker had dit jaar al een verhuisdag op ${datumVoluitZonderJaar(isoDatum)}.`;
}
